import React, { useState, useRef, useEffect } from 'react';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import Dashboard from './Dashboard';
import Leads from './Leads';
import Oportunidades from './Oportunidades';
import Presupuestos from './Presupuestos';
import Clientes from './Clientes';
import Proyectos from './Proyectos';
import Horas from './Horas';
import Analisis from './Analisis';
import Configuracion from './Configuracion';
import { DatosProvider } from './DatosContext';
import { PerfilContext } from './SolicitudEliminacion';

// ─── Iconos del sidebar ────────────────────────────────────────────────────
const IconDashboard = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor"/>
    <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor"/>
    <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor"/>
    <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor"/>
  </svg>
);
const IconLeads = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="8" cy="5.5" r="2.5"/>
    <path d="M3 14c0-3 2.2-4.5 5-4.5s5 1.5 5 4.5"/>
  </svg>
);
const IconOportunidades = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 1l1.854 3.756L14 5.528l-3 2.924.708 4.132L8 10.5l-3.708 2.084L5 8.452 2 5.528l4.146-.772z"/>
  </svg>
);
const IconPresupuestos = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <line x1="8" y1="1" x2="8" y2="15"/>
    <path d="M11 4.5C11 3 9.5 2.5 8 2.5 6 2.5 5 3.5 5 5c0 1.5 1 2 3 2.5 2 .5 3.5 1 3.5 3 0 1.5-1.5 3-3.5 3-2 0-3.5-1-3.5-2.5"/>
  </svg>
);
const IconClientes = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <circle cx="8" cy="4.5" r="3.5"/>
    <path d="M1.5 15c0-3.5 2.9-5.5 6.5-5.5s6.5 2 6.5 5.5"/>
  </svg>
);
const IconProyectos = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 7L8 1.5L15 7"/>
    <path d="M2.5 6.5V14H6V10H10V14H13.5V6.5"/>
    <rect x="6.5" y="10" width="3" height="4"/>
  </svg>
);
const IconHoras = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="8" cy="8" r="6.5"/>
    <polyline points="8,4 8,8 11,10"/>
  </svg>
);
const IconAnalisis = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="1"  y="8"  width="3" height="7" rx="1" fill="currentColor" opacity="0.7"/>
    <rect x="6"  y="4"  width="3" height="11" rx="1" fill="currentColor"/>
    <rect x="11" y="1"  width="3" height="14" rx="1" fill="currentColor" opacity="0.85"/>
  </svg>
);
const IconConfiguracion = () => (
  <span style={{ fontSize: '1rem' }}>⚙</span>
);

// ─── Definición de módulos con visibilidad por roles ───────────────────────
const MODULOS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icono: <IconDashboard />,
    rolesPermitidos: [],
  },
  {
    id: 'leads',
    label: 'Leads',
    icono: <IconLeads />,
    rolesPermitidos: ['admin', 'comercial'],
  },
  {
    id: 'oportunidades',
    label: 'Oportunidades',
    icono: <IconOportunidades />,
    rolesPermitidos: ['admin', 'comercial'],
  },
  {
    id: 'presupuestos',
    label: 'Presupuestos',
    icono: <IconPresupuestos />,
    rolesPermitidos: ['admin', 'comercial'],
  },
  {
    id: 'clientes',
    label: 'Clientes',
    icono: <IconClientes />,
    rolesPermitidos: ['admin', 'comercial'],
  },
  {
    id: 'proyectos',
    label: 'Proyectos',
    icono: <IconProyectos />,
    rolesPermitidos: ['admin', 'comercial'], // sesión 33: los empleados no ven Proyectos
  },
  {
    id: 'horas',
    label: 'Control de horas',
    icono: <IconHoras />,
    rolesPermitidos: ['admin', 'empleado'],
  },
  {
    id: 'analisis',
    label: 'Análisis',
    icono: <IconAnalisis />,
    rolesPermitidos: ['admin', 'comercial'],
  },
  {
    id: 'configuracion',
    label: 'Configuración',
    icono: <IconConfiguracion />,
    rolesPermitidos: ['admin'],
  },
];

// ─── Helpers de rol ────────────────────────────────────────────────────────
export function tieneRol(perfil, rol) {
  if (!perfil?.roles) return false;
  if (Array.isArray(rol)) return rol.some(r => perfil.roles.includes(r));
  return perfil.roles.includes(rol);
}

function etiquetaRol(perfil) {
  if (!perfil?.roles) return 'Empleado';
  if (perfil.roles.includes('admin')) return 'Administrador';
  const etiquetas = [];
  if (perfil.roles.includes('comercial')) etiquetas.push('Comercial');
  if (perfil.roles.includes('empleado')) etiquetas.push('Empleado');
  return etiquetas.join(' · ') || 'Empleado';
}

// ─── Componente Layout ─────────────────────────────────────────────────────
function Layout({ usuario, perfil }) {
  const [moduloActivo, setModuloActivo]   = useState('dashboard');
  const [accionInicial, setAccionInicial] = useState(null);
  const [sidebarAbierto, setSidebarAbierto] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const refMain = useRef(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    if (window.innerWidth <= 768) setSidebarAbierto(false);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const esAdmin     = tieneRol(perfil, 'admin');
  const esComercial = tieneRol(perfil, 'comercial');
  const esEmpleado  = tieneRol(perfil, 'empleado');
  const verDatosEconomicos = esAdmin;

  const handleLogout = async () => { await signOut(auth); };

  // Sesión 34: los datos extra (presupuestoId, origen...) viajan con la acción; antes se perdían por el camino.
  const handleNavegar = ({ modulo, accion = null, id = null, ...extra }) => {
    const mod = MODULOS.find(m => m.id === modulo);
    if (mod && mod.rolesPermitidos.length > 0) {
      const tieneAcceso = mod.rolesPermitidos.some(r => perfil?.roles?.includes(r));
      if (!tieneAcceso) return;
    }
    setAccionInicial(accion ? { accion, id, ...extra } : null);
    setModuloActivo(modulo);
  };

  const handleAccionCompletada = () => setAccionInicial(null);

  const handleCambiarModulo = (id) => {
    setAccionInicial(null);
    setModuloActivo(id);
  };

  const modulosVisibles = MODULOS.filter(m => {
    if (m.rolesPermitidos.length === 0) return true;
    return m.rolesPermitidos.some(r => perfil?.roles?.includes(r));
  });

  const renderModulo = () => {
    switch (moduloActivo) {
      case 'dashboard':
        return (
          <Dashboard
            perfil={perfil}
            usuario={usuario}
            esAdmin={esAdmin}
            esComercial={esComercial}
            esEmpleado={esEmpleado}
            onNavegar={handleNavegar}
          />
        );
      case 'leads':
        return (
          <Leads
            esAdmin={esAdmin}
            accionInicial={accionInicial}
            onAccionCompletada={handleAccionCompletada}
            onNavegar={handleNavegar}
          />
        );
      case 'oportunidades':
        return (
          <Oportunidades
            esAdmin={esAdmin}
            accionInicial={accionInicial}
            onAccionCompletada={handleAccionCompletada}
            onNavegarLead={(lead) => handleNavegar({ modulo: 'leads', accion: 'ficha', id: lead.id })}
            onNavegar={handleNavegar}
          />
        );
      case 'presupuestos':
        return (
          <Presupuestos
            esAdmin={esAdmin}
            accionInicial={accionInicial}
            onAccionCompletada={handleAccionCompletada}
            onNavegar={handleNavegar}
          />
        );
      case 'clientes':
        return (
          <Clientes
            esAdmin={esAdmin}
            verDatosEconomicos={verDatosEconomicos}
            accionInicial={accionInicial}
            onAccionCompletada={handleAccionCompletada}
          />
        );
      case 'proyectos':
        return (
          <Proyectos
            esAdmin={esAdmin}
            verDatosEconomicos={verDatosEconomicos}
            accionInicial={accionInicial}
            onAccionCompletada={handleAccionCompletada}
            onNavegar={handleNavegar}
          />
        );
      case 'horas':
        return (
          <Horas
            perfil={perfil}
            usuario={usuario}
            esAdmin={esAdmin}
            accionInicial={accionInicial}
            onAccionCompletada={handleAccionCompletada}
          />
        );
      case 'analisis':
        return (
          <Analisis
            refContenedor={refMain}
            esAdmin={esAdmin}
            soloCRM={!esAdmin && esComercial}
          />
        );
      case 'configuracion':
        return esAdmin
          ? <Configuracion perfil={perfil} usuario={usuario} />
          : null;
      default:
        return (
          <Dashboard
            perfil={perfil}
            usuario={usuario}
            esAdmin={esAdmin}
            esComercial={esComercial}
            esEmpleado={esEmpleado}
            onNavegar={handleNavegar}
          />
        );
    }
  };

  return (
    <PerfilContext.Provider value={perfil}>
    <DatosProvider refMain={refMain}>
    <div style={styles.root}>
      {/* ── Sidebar ── */}
      <aside style={{
        ...styles.sidebar,
        width: sidebarAbierto ? '220px' : '64px',
        ...(isMobile ? {
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          zIndex: 200,
          overflowY: 'auto',
          overflowX: 'hidden',
        } : {})
      }}>

        {/* Logo */}
        <div style={styles.logoArea}>
          <svg width="44" height="44" viewBox="0 0 48 48" fill="none" style={{ flexShrink: 0 }}>
            <circle cx="24" cy="24" r="20" stroke="#7B6FE8" strokeWidth="2.5"
              strokeLinecap="round" strokeDasharray="100 16" strokeDashoffset="8" />
            <line x1="24" y1="14" x2="24" y2="34" stroke="#7B6FE8" strokeWidth="2.5" strokeLinecap="round"/>
            <line x1="14" y1="24" x2="34" y2="24" stroke="#7B6FE8" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          {sidebarAbierto && <span style={styles.logoText}>METRON</span>}
        </div>

        {/* Toggle */}
        <button onClick={() => setSidebarAbierto(!sidebarAbierto)} style={styles.toggleBtn}>
          {sidebarAbierto ? '←' : '→'}
        </button>

        {/* Navegación */}
        <nav style={styles.nav}>
          {modulosVisibles.map(m => (
            <button
              key={m.id}
              onClick={() => handleCambiarModulo(m.id)}
              style={{
                ...styles.navItem,
                background: moduloActivo === m.id ? '#534AB7' : 'transparent',
                color:      moduloActivo === m.id ? '#ffffff'  : '#AFA9EC',
                justifyContent: sidebarAbierto ? 'flex-start' : 'center',
              }}
            >
              <span style={styles.navIcono}>{m.icono}</span>
              {sidebarAbierto && <span style={styles.navLabel}>{m.label}</span>}
            </button>
          ))}
        </nav>

        {/* Usuario y logout */}
        <div style={styles.userArea}>
          <div style={styles.avatar}>
            {(perfil?.nombre || usuario.email).charAt(0).toUpperCase()}
          </div>
          {sidebarAbierto && (
            <div style={styles.userInfo}>
              <span style={styles.userName}>{perfil?.nombre || usuario.email}</span>
              <span style={styles.userRol}>{etiquetaRol(perfil)}</span>
            </div>
          )}
          {sidebarAbierto && (
            <button onClick={handleLogout} style={styles.logoutBtn} title="Cerrar sesión">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#AFA9EC"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          )}
        </div>
      </aside>

      {/* ── Contenido principal ── */}
      <main ref={refMain} style={{
        ...styles.main,
        ...(isMobile ? { marginLeft: sidebarAbierto ? '220px' : '64px' } : {})
      }}>
        {renderModulo()}
      </main>
    </div>
    </DatosProvider>
    </PerfilContext.Provider>
  );
}

const styles = {
  root:       { display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden', background: '#0e0b24' },
  sidebar:    { background: '#1a1440', display: 'flex', flexDirection: 'column', transition: 'width 0.25s ease', overflow: 'hidden', flexShrink: 0, borderRight: '1px solid rgba(83,74,183,0.2)', position: 'relative', zIndex: 100 },
  logoArea:   { display: 'flex', alignItems: 'center', gap: '12px', padding: '20px 16px', borderBottom: '1px solid rgba(83,74,183,0.25)', justifyContent: 'center' },
  logoText:   { fontSize: '1.05rem', fontWeight: '300', letterSpacing: '0.18em', color: '#ffffff', whiteSpace: 'nowrap' },
  toggleBtn:  { background: 'none', border: 'none', color: '#AFA9EC', fontSize: '1rem', padding: '8px 16px', textAlign: 'right', width: '100%', cursor: 'pointer' },
  nav:        { display: 'flex', flexDirection: 'column', gap: '2px', padding: '8px', flex: 1, overflowY: 'auto' },
  navItem:    { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', border: 'none', fontSize: '0.88rem', fontWeight: '400', cursor: 'pointer', transition: 'background 0.15s, color 0.15s', whiteSpace: 'nowrap', width: '100%', textAlign: 'left' },
  navIcono:   { fontSize: '1rem', flexShrink: 0, width: '20px', textAlign: 'center' },
  navLabel:   { overflow: 'hidden', textOverflow: 'ellipsis' },
  userArea:   { display: 'flex', alignItems: 'center', gap: '10px', padding: '16px', borderTop: '1px solid rgba(83,74,183,0.2)' },
  avatar:     { width: '32px', height: '32px', borderRadius: '50%', background: '#534AB7', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: '500', flexShrink: 0 },
  userInfo:   { display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' },
  userName:   { color: '#ffffff', fontSize: '0.82rem', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  userRol:    { color: '#AFA9EC', fontSize: '0.72rem' },
  logoutBtn:  { background: 'none', border: 'none', color: '#AFA9EC', fontSize: '1rem', cursor: 'pointer', flexShrink: 0, padding: '4px' },
  main:       { flex: 1, overflow: 'auto', background: '#0e0b24' },
};

export default Layout;
